/**
 * Anonymous Header Component
 *
 * Header for anonymous users (not logged in) with logo, auth buttons, theme toggle, and language switcher.
 *
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import { useAuth } from '../../pages/Auth';
import { Button } from '../Button';
import { Avatar } from '../Avatar';
import { LanguageSwitcher } from '../LanguageSwitcher';

export interface AnonymousHeaderProps {
  className?: string;
}

export const AnonymousHeader: React.FC<AnonymousHeaderProps> = ({ className }) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/register');
  };

  const handleProfileAction = (action: string) => {
    if (action === 'profile') {
      navigate('/profile-settings');
    } else if (action === 'organization') {
      navigate('/organization');
    }
    setIsProfileOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileAction = (action: () => void) => {
    action();
    setIsMobileMenuOpen(false);
  };

  const profileItems = [
    {
      key: 'profile',
      label: t('profile.title'),
      icon: 'person',
      onClick: () => handleProfileAction('profile'),
    },
    {
      key: 'organization',
      label: t('organization.title'),
      icon: 'business',
      onClick: () => handleProfileAction('organization'),
    },
    {
      key: 'divider',
      label: '',
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: t('auth.signOut'),
      icon: 'logout',
      onClick: () => navigate('/sign-out'),
    },
  ];

  const currentUser = user
    ? {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        role: user.role,
        avatar: undefined,
      }
    : {
        name: 'John Doe',
        email: 'john.doe@acmecorp.com',
        role: 'Organization Admin',
      };

  return (
    <header
      className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm ${className || ''}`}
    >
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left Section - Logo */}
        <div className="flex items-center">
          <h1 className="text-xl sm:text-2xl font-bold font-display leading-30 tracking-0.50 bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
            VoxAI
          </h1>
        </div>

        {/* Desktop Right Section - Auth Buttons or Profile Menu */}
        <div className="hidden md:flex items-center space-x-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">
              {isDarkMode ? 'dark_mode' : 'light_mode'}
            </span>
          </Button>

          {/* Language Switcher */}
          <LanguageSwitcher />

          {isAuthenticated ? (
            /* Profile Menu */
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsProfileOpen(!isProfileOpen);
                  }
                }}
                className="cursor-pointer p-1 border-none bg-transparent"
                aria-label="Toggle profile menu"
              >
                <Avatar
                  size="sm"
                  src={currentUser.avatar}
                  alt={currentUser.name}
                  className="shadow-none"
                >
                  {currentUser.name
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')}
                </Avatar>
              </button>

              {/* Profile Dropdown */}
              {isProfileOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  {/* User Info Header */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-semibold leading-27 text-gray-900 dark:text-white">
                      {currentUser.name} ({currentUser.email})
                    </div>
                    <div className="text-xs font-medium leading-27 text-primary-600 dark:text-primary-400">
                      Owner
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    {profileItems.map(item => {
                      if (item.type === 'divider') {
                        return (
                          <div
                            key={item.key}
                            className="border-t border-gray-200 dark:border-gray-700 my-2"
                          />
                        );
                      }

                      return (
                        <button
                          key={item.key}
                          onClick={item.onClick}
                          className="w-full flex items-center px-4 py-2 text-sm font-medium leading-27 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <span className="material-icons text-lg mr-3 text-gray-500 dark:text-gray-400">
                            {item.icon}
                          </span>
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Authentication Buttons */
            <>
              {/* Sign In Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignIn}
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                {t('auth.signIn')}
              </Button>

              {/* Sign Up Button */}
              <Button variant="gradient-multi" size="sm" onClick={handleSignUp} className="px-4">
                {t('auth.register')}
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center space-x-2">
          {/* Theme Toggle - Mobile */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">
              {isDarkMode ? 'dark_mode' : 'light_mode'}
            </span>
          </Button>

          {/* Language Switcher - Mobile */}
          <LanguageSwitcher />

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMobileMenuToggle}
            className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            aria-label="Toggle mobile menu"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">
              {isMobileMenuOpen ? 'close' : 'menu'}
            </span>
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
        >
          <div className="px-4 py-3 space-y-3">
            {isAuthenticated ? (
              /* Mobile Profile Section */
              <>
                {/* User Info */}
                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Avatar
                    size="sm"
                    src={currentUser.avatar}
                    alt={currentUser.name}
                    className="shadow-none"
                  >
                    {currentUser.name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')}
                  </Avatar>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {currentUser.name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {currentUser.email}
                    </div>
                  </div>
                </div>

                {/* Mobile Profile Menu Items */}
                <div className="space-y-1">
                  {profileItems.map(item => {
                    if (item.type === 'divider') {
                      return (
                        <div
                          key={item.key}
                          className="border-t border-gray-200 dark:border-gray-700 my-2"
                        />
                      );
                    }

                    return (
                      <button
                        key={item.key}
                        onClick={() => handleMobileAction(item.onClick)}
                        className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <span className="material-icons text-lg mr-3 text-gray-500 dark:text-gray-400">
                          {item.icon}
                        </span>
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              /* Mobile Auth Buttons */
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMobileAction(handleSignIn)}
                  className="w-full justify-start text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
                >
                  <span className="material-icons text-lg mr-3">login</span>
                  {t('auth.signIn')}
                </Button>
                <Button
                  variant="gradient-multi"
                  size="sm"
                  onClick={() => handleMobileAction(handleSignUp)}
                  className="w-full justify-start"
                >
                  <span className="material-icons text-lg mr-3">person_add</span>
                  {t('auth.register')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
